import type { ReadinessResult, CategoryScore, ReadinessGrade, ReadinessInput } from './types';
import { DEADWEIGHT_TERMS, TIER_1_GENRE_ANCHORS, TIER_2_BEHAVIORAL, TIER_3_MOOD, TIER_4_SOFT_TECHNICAL } from './vocabularyData';
import { GENRE_CONFLICT_MATRIX, VOCAL_CONFLICT_PAIRS } from './conflictData';

const FILLER_WORDS = new Set([
  'the', 'a', 'an', 'and', 'or', 'but', 'with', 'using', 'very', 'really',
  'quite', 'some', 'that', 'which', 'in', 'of', 'for', 'to', 'is', 'are',
  'was', 'were', 'be', 'been', 'being', 'have', 'has', 'had', 'do', 'does',
  'did', 'will', 'would', 'could', 'should', 'may', 'might', 'shall',
  'can', 'not', 'no', 'so', 'if', 'then', 'than', 'this', 'it', 'its',
  'at', 'by', 'on', 'up', 'as', 'into', 'from', 'about', 'like',
]);

function gradeFromScore(score: number): ReadinessGrade {
  if (score >= 80) return 'strong';
  if (score >= 50) return 'moderate';
  return 'weak';
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function containsTerm(text: string, term: string): boolean {
  return text.toLowerCase().includes(term.toLowerCase());
}

/**
 * Whole-word match for dead-weight terms (AUTO-098). A plain includes() made "ratio" fire inside
 * "saturation", "db" inside "feedback", "cents" inside "accents", "daw" inside "dawn". A letter on either
 * side breaks the match; a digit or "_" does not, so "3kHz" and "Neve_8078_..." still count.
 */
function hasDeadWeightTerm(text: string, term: string): boolean {
  const escaped = term.toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`(?<![a-z])${escaped}(?![a-z])`, 'i').test(text);
}

function scoreLength(styleText: string): CategoryScore {
  const words = styleText.trim().split(/\s+/).filter(Boolean);
  const count = words.length;
  const issues: string[] = [];
  const positives: string[] = [];
  let score: number;

  if (count < 10) {
    score = 20;
    issues.push(`Only ${count} words — too thin for Suno to lock onto a clear pattern`);
  } else if (count < 20) {
    score = 50;
    issues.push(`${count} words — workable but more descriptors would help Suno nail the vibe`);
  } else if (count < 40) {
    score = 75;
    positives.push(`${count} words — acceptable density`);
  } else if (count <= 80) {
    score = 100;
    positives.push(`${count} words — sweet spot for signal density`);
  } else if (count <= 120) {
    score = 85;
    issues.push(`${count} words — getting verbose, Suno weights early tokens more`);
  } else if (count <= 200) {
    score = 60;
    issues.push(`${count} words — signal is diluted, strongest descriptors are competing`);
  } else {
    score = 25;
    issues.push(`${count} words — signal noise floor, Suno can't distinguish what matters`);
  }

  return { label: 'Prompt Length', score, weight: 0.10, grade: gradeFromScore(score), issues, positives };
}

function scoreGenreConflicts(styleText: string, blends: string[]): CategoryScore {
  const combined = (styleText + ' ' + blends.join(' ')).toLowerCase();
  const found: [string, string][] = [];
  const issues: string[] = [];
  const positives: string[] = [];

  for (const [a, b] of GENRE_CONFLICT_MATRIX) {
    if (containsTerm(combined, a) && containsTerm(combined, b)) {
      found.push([a, b]);
      issues.push(`"${a}" + "${b}" — these cancel each other in Suno's pattern space`);
    }
  }

  const penalty = found.length * 35;
  const score = clamp(100 - penalty, 0, 100);
  if (found.length === 0) positives.push('No conflicting genre signals detected');

  return { label: 'Genre Conflicts', score, weight: 0.20, grade: gradeFromScore(score), issues, positives };
}

function scoreDeadWeightJargon(styleText: string): CategoryScore {
  const lower = styleText.toLowerCase();
  const found: string[] = [];
  const issues: string[] = [];
  const positives: string[] = [];

  for (const term of DEADWEIGHT_TERMS) {
    if (hasDeadWeightTerm(lower, term)) {
      found.push(term);
    }
  }

  let score = 100;
  score -= found.length * 15;
  if (found.length >= 3) score -= 20;
  score = clamp(score, 0, 100);

  if (found.length > 0) {
    issues.push(`Found ${found.length} technical term${found.length > 1 ? 's' : ''} Suno has no training association for`);
  } else {
    positives.push('No dead-weight DAW jargon detected');
  }

  return { label: 'Dead-Weight Jargon', score, weight: 0.20, grade: gradeFromScore(score), issues, positives };
}

function scoreTokenPosition(styleText: string): CategoryScore {
  const tokens = styleText.split(/[,;]/).map(t => t.trim()).filter(Boolean);
  const issues: string[] = [];
  const positives: string[] = [];

  if (tokens.length === 0) {
    return { label: 'Token Position', score: 0, weight: 0.15, grade: 'weak', issues: ['No tokens found'], positives };
  }

  const earlyTokens = tokens.slice(0, 3);
  let earlyAnchors = 0;
  let earlyTechnical = 0;

  for (const token of earlyTokens) {
    const lower = token.toLowerCase();
    if (TIER_1_GENRE_ANCHORS.some(g => lower.includes(g))) earlyAnchors++;
    else if (TIER_2_BEHAVIORAL.some(b => lower.includes(b))) earlyAnchors++;
    else if (DEADWEIGHT_TERMS.some(d => hasDeadWeightTerm(lower, d))) earlyTechnical++;
  }

  let score: number;
  if (earlyAnchors >= 2) {
    score = 100;
    positives.push('Genre/mood anchors lead the prompt — Suno will lock on fast');
  } else if (earlyAnchors >= 1 && earlyTechnical === 0) {
    score = 75;
    positives.push('Decent lead — a genre anchor in the first few tokens');
  } else if (earlyTechnical >= 2) {
    score = 20;
    issues.push('Technical specs lead the prompt — Suno weights early tokens highest and these mean nothing to it');
  } else {
    score = 50;
    issues.push('Consider leading with your strongest genre/mood descriptor');
  }

  return { label: 'Token Position', score, weight: 0.15, grade: gradeFromScore(score), issues, positives };
}

function scoreSignalDensity(styleText: string): CategoryScore {
  const words = styleText.toLowerCase().split(/\s+/).filter(Boolean);
  const issues: string[] = [];
  const positives: string[] = [];

  if (words.length === 0) {
    return { label: 'Signal Density', score: 0, weight: 0.15, grade: 'weak', issues: ['Empty prompt'], positives };
  }

  const meaningful = words.filter(w => !FILLER_WORDS.has(w));
  const density = meaningful.length / words.length;
  const score = clamp(Math.round(density * 100), 0, 100);

  if (density >= 0.8) {
    positives.push(`${Math.round(density * 100)}% signal density — tight and focused`);
  } else if (density >= 0.6) {
    positives.push(`${Math.round(density * 100)}% signal density — decent`);
  } else {
    issues.push(`Only ${Math.round(density * 100)}% meaningful words — too much filler diluting the signal`);
  }

  return { label: 'Signal Density', score, weight: 0.15, grade: gradeFromScore(score), issues, positives };
}

function scoreVocalConflicts(lyricsText: string): CategoryScore {
  const lower = lyricsText.toLowerCase();
  const found: [string, string][] = [];
  const issues: string[] = [];
  const positives: string[] = [];

  for (const [a, b] of VOCAL_CONFLICT_PAIRS) {
    if (containsTerm(lower, a) && containsTerm(lower, b)) {
      found.push([a, b]);
      issues.push(`"${a}" + "${b}" — contradictory vocal instructions`);
    }
  }

  const penalty = found.length * 40;
  const score = clamp(100 - penalty, 0, 100);
  if (found.length === 0) positives.push('No contradictory vocal instructions');

  return { label: 'Vocal Conflicts', score, weight: 0.10, grade: gradeFromScore(score), issues, positives };
}

function scoreVocabulary(styleText: string): CategoryScore {
  const words = styleText.toLowerCase().split(/[\s,;]+/).filter(Boolean);
  const issues: string[] = [];
  const positives: string[] = [];
  const strongTerms: string[] = [];

  let tier1 = 0, tier2 = 0, tier3 = 0, tier4 = 0, tier5 = 0;

  // Check individual words and bigrams
  for (let i = 0; i < words.length; i++) {
    const word = words[i];
    const bigram = i < words.length - 1 ? `${word} ${words[i + 1]}` : '';

    const checkTerm = (term: string) => {
      if (DEADWEIGHT_TERMS.some(d => hasDeadWeightTerm(term, d))) { tier5++; return; }
      if (TIER_1_GENRE_ANCHORS.some(g => term.includes(g))) { tier1++; strongTerms.push(term); return; }
      if (TIER_2_BEHAVIORAL.some(b => term === b || term.includes(b))) { tier2++; strongTerms.push(term); return; }
      if (TIER_3_MOOD.some(m => term.includes(m))) { tier3++; return; }
      if (TIER_4_SOFT_TECHNICAL.some(t => term.includes(t))) { tier4++; return; }
    };

    checkTerm(word);
    if (bigram) checkTerm(bigram);
  }

  const totalRecognized = tier1 + tier2 + tier3 + tier4;
  const expectedTokens = Math.max(words.length / 2, 1);
  const rawScore = ((tier1 * 4) + (tier2 * 3) + (tier3 * 2) + (tier4 * 1) - (tier5 * 3)) / expectedTokens * 25;
  const score = clamp(Math.round(rawScore), 0, 100);

  if (tier1 > 0) positives.push(`${tier1} strong genre anchor${tier1 > 1 ? 's' : ''} detected`);
  if (tier2 > 0) positives.push(`${tier2} behavioral descriptor${tier2 > 1 ? 's' : ''} Suno responds to`);
  if (tier5 > 0) issues.push(`${tier5} dead-weight technical term${tier5 > 1 ? 's' : ''} dragging score down`);
  if (totalRecognized === 0 && tier5 === 0) issues.push('No recognized Suno vocabulary found');

  return { label: 'Vocabulary Quality', score, weight: 0.10, grade: gradeFromScore(score), issues, positives };
}

export function analyzePrompt(input: ReadinessInput): ReadinessResult {
  const { styleText, lyricsText, blends } = input;

  const length = scoreLength(styleText);
  const genreConflict = scoreGenreConflicts(styleText, blends);
  const deadWeightJargon = scoreDeadWeightJargon(styleText);
  const tokenPosition = scoreTokenPosition(styleText);
  const signalDensity = scoreSignalDensity(styleText);
  const vocalConflict = scoreVocalConflicts(lyricsText);
  const vocabularyScore = scoreVocabulary(styleText);

  const categories = { length, genreConflict, deadWeightJargon, tokenPosition, signalDensity, vocalConflict, vocabularyScore };

  const totalScore = Math.round(
    Object.values(categories).reduce((sum, cat) => sum + cat.score * cat.weight, 0)
  );

  // Collect found items
  const deadWeightTermsFound: string[] = [];
  const lower = styleText.toLowerCase();
  for (const term of DEADWEIGHT_TERMS) {
    if (hasDeadWeightTerm(lower, term)) deadWeightTermsFound.push(term);
  }

  const conflictingGenresFound: [string, string][] = [];
  const combinedGenre = (styleText + ' ' + blends.join(' ')).toLowerCase();
  for (const [a, b] of GENRE_CONFLICT_MATRIX) {
    if (containsTerm(combinedGenre, a) && containsTerm(combinedGenre, b)) {
      conflictingGenresFound.push([a, b]);
    }
  }

  const conflictingVocalsFound: [string, string][] = [];
  const lyricsLower = lyricsText.toLowerCase();
  for (const [a, b] of VOCAL_CONFLICT_PAIRS) {
    if (containsTerm(lyricsLower, a) && containsTerm(lyricsLower, b)) {
      conflictingVocalsFound.push([a, b]);
    }
  }

  const strongTermsFound: string[] = [];
  const styleWords = styleText.toLowerCase().split(/[\s,;]+/).filter(Boolean);
  for (const word of styleWords) {
    if (TIER_1_GENRE_ANCHORS.some(g => word.includes(g)) || TIER_2_BEHAVIORAL.some(b => word === b)) {
      if (!strongTermsFound.includes(word)) strongTermsFound.push(word);
    }
  }

  return {
    totalScore,
    grade: gradeFromScore(totalScore),
    wordCount: styleText.trim().split(/\s+/).filter(Boolean).length,
    categories,
    deadWeightTermsFound,
    conflictingGenresFound,
    conflictingVocalsFound,
    strongTermsFound,
  };
}
